/* eslint-disable @next/next/no-img-element */
/* eslint-disable react/require-default-props */
/* eslint-disable no-alert */
/* eslint-disable react/no-danger */
import {
  Avatar,
  CardHighlight,
  CardType,
  DiscussionCard,
  DiscussionCardHeader,
  MenuOption,
  ThreadMetrics,
  Vote,
} from '@amzn/cuminuicomponents';
import {
  KatAlert, KatButton, KatIcon, KatPopover,
} from '@amzn/katal-react';
import { useRouter } from 'next/router';
import PinIcon from 'public/images/icons/pin.svg';
import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmationModal, { ConfirmationType } from 'src/components/thread-detail/ConfirmationModal';
import PinThreadModal from 'src/components/thread-detail/PinThreadModal';
import PostBanners from 'src/components/thread-detail/PostBanners';
import PostDate from 'src/components/thread-detail/PostDate';
import { DiscussionPostErrors } from 'src/constants/errorStrings';
import { ButtonTypes, Logs } from 'src/constants/metrics';
import {
  getGroupListing, getThreadDetail, getThreadDetailEdit, THREAD_LISTING,
} from 'src/constants/pages';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import useValidateUserAction from 'src/redux/hooks/useValidateUserAction';
import {
  markSolution,
  selectDraftReplyId,
  selectMarkSolutionStatus,
  selectPostById,
  selectThreadDetailOp,
  selectThreadDetailPostIds,
  selectThreadDetailPosts,
  submitVote,
  threadDetailActions,
} from 'src/redux/slices/threadDetailSlice';
import { AppState } from 'src/redux/store';
import {
  DraftPostType,
  OpPost,
  PostErrors,
  PostState,
  PostSuccess,
  PostType,
  ReplyPost,
  VoteOptions,
} from 'src/types/DiscussionPost';
import FetchStatus from 'src/types/FetchStatus';
import { QuoteRef } from 'src/types/Quotes';
import {
  createProfileHref, logger, transformHtmlToReact, useMetrics,
} from 'src/utils';
import isPinned from 'src/utils/isPinned';
import ActionType from 'src/redux/hooks/types';
import ModalImageCarousel from 'src/components/image-carousel/ModalImageCarousel';
import MenuOptions, { MenuTypes } from './MenuOptions';
import FlagPostModal from './FlagPostModal';

interface DiscussionPostProps {
  postId: string;
  quoteRef?: QuoteRef;
  postAction?: ReactElement;
  isShowRepliesVisible?: boolean;
}

const DiscussionPost: React.FC<DiscussionPostProps> = ({
  postId,
  postAction,
  quoteRef,
  isShowRepliesVisible = true,
}) => {
  const { t } = useTranslation();
  const { clickMetric } = useMetrics();
  const { push, basePath } = useRouter();
  const [flagPost, setFlagPost] = useState({ isVisible: false, showSuccess: false });
  const [pinThread, setPinThread] = useState({ isVisible: false, showSuccess: false });
  const [confirmationType, setConfirmationType] = useState<ConfirmationType | undefined>(undefined);
  const dispatch = useAppDispatch();
  const post = useAppSelector((state: AppState) => selectPostById(state, postId));
  const opPost = useAppSelector(selectThreadDetailOp);
  const categories = useAppSelector((state: AppState) => state.threadCategories.categories);
  const tagsList = useAppSelector((state: AppState) => state.threadCategories.tags);
  const { userId } = useAppSelector(({ userInfo }: AppState) => userInfo);
  const draftReplyId = useAppSelector(selectDraftReplyId);
  const markSolutionStatus = useAppSelector(selectMarkSolutionStatus);
  const { groups } = useAppSelector((state) => state.groups);
  const [voteSubmissionStatus, setVoteSubmissionStatus] = useState<FetchStatus>(FetchStatus.idle);
  if (!post) {
    const parentPostIdsStore = useAppSelector(selectThreadDetailPostIds);
    const postIdsStore = Object.keys(useAppSelector(selectThreadDetailPosts));
    logger.info(Logs.discussionPostError, {
      postId,
      href: window.location.href,
      parentPostIdsStore,
      postIdsStore,
    });
    return null;
  }
  const {
    errors,
    success,
    showReplies,
    postDetails,
  } = post;
  const {
    id,
    userId: postUserId,
    username: postUserName,
    avatarSrc,
    upVoteCount,
    downVoteCount,
    isDeleted,
    title,
    createdAt,
    updatedAt,
    viewCount,
    content,
    contentMetadata,
    replyCount,
    categoryId,
    tags: tagIds,
    banners,
    replyToUsername,
    replyToId,
    postType,
    isFlagged,
    threadId,
    voteDecision,
    updatedByModerator,
    isAmazonReplied,
    isSolution,
    isMigrated,
  } = postDetails as OpPost & ReplyPost;
  const isAuthor = !!userId && !!postUserId && userId === postUserId;
  const linkToProfile = postUserId ? createProfileHref(postUserId) : undefined;
  const cardType = postType === PostType.op ? CardType.original : CardType.reply;
  const { groupId } = opPost;
  const isNotGroupMember = !!groupId && !groups[groupId]?.isMember;
  const { validateUserAction } = useValidateUserAction({ postId });
  const isInternalUser = useAppSelector(({ userInfo }: AppState) => userInfo.isInternal);
  const [imageCarousel, setImageCarousel] = useState({ visible: false, index: 0 });
  const imageList: { id: string, src: string }[] = [];

  useEffect(() => {
    const successCopyIndex = success.findIndex((sucessItem) => sucessItem === PostSuccess.copyPost);
    let copySuccessTimer: ReturnType<typeof setTimeout>;
    if (successCopyIndex > -1) {
      copySuccessTimer = setTimeout(() => dispatch(threadDetailActions.removeSuccess(
        { postId, successIndex: successCopyIndex },
      )), 1000);
    }
    return () => clearTimeout(copySuccessTimer);
  }, [success]);

  // Card menu options (kebab menu)
  const getMenuOptions = () => {
    const cardMenuOptions: Array<MenuOption> = [
      {
        label: MenuOptions[
          success.includes(PostSuccess.copyPost) ? MenuTypes.copySuccess : MenuTypes.copy
        ],
        onClick: () => dispatch(threadDetailActions.setCopyPostLink(postId)),
      },
    ];

    if (opPost.userId === userId
      && !!userId
      && !opPost.solutionId
      && postType !== PostType.op
      && !isMigrated
      && !isDeleted
    ) {
      cardMenuOptions.push(
        {
          label: MenuOptions[MenuTypes.markSolution],
          onClick: (setVisible) => {
            setVisible(false);
            validateUserAction(() => markSolutionStatus !== FetchStatus.loading && dispatch(markSolution({ threadId, postId, solutionAction: 'MARK' })));
          },
        },
      );
    }

    if (opPost.userId === userId
      && !!userId
      && opPost.solutionId === postId
      && !isMigrated
      && !isDeleted
    ) {
      cardMenuOptions.push(
        {
          label: MenuOptions[MenuTypes.unmarkSolution],
          onClick: (setVisible) => {
            setVisible(false);
            validateUserAction(() => markSolutionStatus !== FetchStatus.loading && dispatch(markSolution({ threadId, postId, solutionAction: 'UNMARK' })));
          },
        },
      );
    }

    if (isInternalUser && postType === PostType.op && !isDeleted) {
      cardMenuOptions.push(
        {
          label: MenuOptions[MenuTypes.pin],
          onClick: (setVisible) => {
            validateUserAction(() => setPinThread({ ...pinThread, isVisible: true }));
            setVisible(false);
          },
        },
      );
    }

    if (isAuthor && !isMigrated && !isDeleted) {
      if (postType === PostType.op && replyCount === 0) {
        if (opPost.postState !== PostState.closed) {
          cardMenuOptions.push(
            {
              label: MenuOptions[MenuTypes.edit],
              onClick: () => validateUserAction(() => push(getThreadDetailEdit(threadId))),
            },
          );
        }
        cardMenuOptions.push({
          label: MenuOptions[MenuTypes.delete],
          onClick: (setVisible) => {
            setVisible(false);
            validateUserAction(
              () => setConfirmationType(ConfirmationType.delete),
              ActionType.POST_DELETE,
            );
          },
        });
      }
      if (postType !== PostType.op && !isSolution) {
        cardMenuOptions.push(
          {
            label: MenuOptions[MenuTypes.edit],
            onClick: (setVisible) => {
              setVisible(false);
              validateUserAction(() => {
                dispatch(threadDetailActions.setDraftPost({
                  parentPostId: replyToId,
                  id: postId,
                  type: DraftPostType.edit,
                }));
                dispatch(threadDetailActions.setDraftContent({
                  content,
                  metadata: contentMetadata,
                }));
              });
            },
          },
          {
            label: MenuOptions[MenuTypes.delete],
            onClick: (setVisible) => {
              setVisible(false);
              validateUserAction(
                () => setConfirmationType(ConfirmationType.delete),
              );
            },
          },
        );
      }
    } else if (!isFlagged && !isDeleted) {
      cardMenuOptions.push(
        {
          label: MenuOptions[MenuTypes.flag],
          onClick: (setVisible) => {
            validateUserAction(
              () => setFlagPost({ ...flagPost, isVisible: true }),
              ActionType.POST_FLAG,
            );
            setVisible(false);
          },
        },
      );
    }
    return cardMenuOptions;
  };

  const errorBanners = errors.length > 0 && (
    <div className="mb-5">
      {
        // filter out all applicable errors that would be rendered within this component
        errors.filter((error) => [
          PostErrors.voteSubmission,
          PostErrors.copyPost,
          PostErrors.deletePost,
          PostErrors.markSolution,
          PostErrors.lockedPost,
          PostErrors.notGroupMember,
          PostErrors.silenced,
          PostErrors.actionOnDeletedPost,
        ].includes(error))
          .map((error, errorIndex) => (
            <KatAlert
              onDismiss={() => dispatch(threadDetailActions.removeError({ errorIndex, postId }))}
              key={error}
              data-testid="failure-alert"
              className="my-1"
              variant="warning"
              header={t(DiscussionPostErrors[error].header)}
              description={t(DiscussionPostErrors[error].description)}
            />
          ))
      }
    </div>
  );

  const getCardHighlight = () => {
    if (isAmazonReplied) {
      return <CardHighlight cardHighlightColorList={['kat-persimmon']} />;
    }
    if (isSolution) {
      return <CardHighlight cardHighlightColorList={['kat-fiji']} />;
    }
    return <CardHighlight cardHighlightColorList={['kat-azure']} />;
  };

  // Card action button logic
  const getCardAction = () => {
    if (postAction) return postAction;
    return (
      <KatButton
        data-testid="discussion-post-reply"
        variant={postType === PostType.op ? 'primary' : 'secondary'}
        onClick={() => validateUserAction(() => {
          dispatch(threadDetailActions.setDraftPost({
            parentPostId: postId,
            type: DraftPostType.reply,
            id: draftReplyId,
          }));
          dispatch(threadDetailActions.setDraftContent({}));
        })}
      >
        <KatIcon slot="icon" name="reply" size="small" />
        {t('replyButton')}
      </KatButton>
    );
  };

  const voteSubmissionHandler = (decision: VoteOptions) => {
    if ([FetchStatus.idle, FetchStatus.complete].includes(voteSubmissionStatus)) {
      validateUserAction(() => {
        setVoteSubmissionStatus(FetchStatus.loading);
        dispatch(submitVote({
          threadId,
          postId,
          voteDecision: decision,
        })).then(() => setVoteSubmissionStatus(FetchStatus.complete));
      }, ActionType.POST_VOTE);
    }
  };

  // Checking if category exists in category-tags map
  const category = categories[categoryId] && {
    label: t(categories[categoryId].nameStringId),
    /**
     * @todo categoryId should be passed to create the url. Once listing page
     * has been setup
     */
    href: `${basePath}${THREAD_LISTING}?categories[]=${categoryId}`,
    onClick: () => clickMetric({
      metricValue: categoryId,
      metricName: ButtonTypes.postCategory,
    }),
  };

  const tags = tagIds && tagsList && tagIds
    .map((tagId) => t(tagsList[tagId]?.stringId))
    .filter((tagStrings) => !!tagStrings);

  const getContent = () => {
    if (isDeleted) {
      const deletedMessage = updatedByModerator
        ? t('discussionCard_postDeleted_byModerator')
        : t('discussionCard_postDeleted');
      return (
        <span className="bg-kat-seattle p-1.5 before:content-['['] after:content-[']']">
          {deletedMessage}
        </span>
      );
    }

    const transformHtmlProps = {
      html: content,
      metadata: contentMetadata,
      quoteRef,
    };

    const transformHtmlWithCarouselProps = {
      ...transformHtmlProps,
      imageCarousel: {
        images: imageList,
        displayImage: (imageId: string) => {
          const imageIndex = imageList.findIndex((img) => img.id === imageId);
          setImageCarousel({
            visible: true,
            index: imageIndex,
          });
        },
      },
    };
    return (
      <div
        ref={(element: HTMLDivElement) => {
          if (quoteRef?.selectable) {
            // eslint-disable-next-line no-param-reassign
            quoteRef.selectable[postId] = element;
          }
        }}
        data-testid="post-content"
        // eslint-disable-next-line tailwindcss/no-custom-classname
        className={`break-words images:max-w-full ${isMigrated ? 'discourse-forums notranslate' : ''}`}
      >
        {transformHtmlToReact(transformHtmlWithCarouselProps)}
      </div>
    );
  };

  const postAuthorClickMetric = () => clickMetric({
    metricValue: postUserId,
    metricName: postType === PostType.op
      ? ButtonTypes.OpPostAuthor : ButtonTypes.replyPostAuthor,
  });

  return (
    <>
      <PostBanners
        banners={banners || []}
        isAuthor={isAuthor}
        isNotGroupMember={postType === PostType.op && isNotGroupMember}
      />
      {
        isSolution && (
          <div className="my-4">
            <KatIcon className="text-kat-fiji" size="small" name="check_circle" />
            <span className="text-[1.125rem] text-kat-fiji ltr:ml-2 rtl:mr-2" slot="header">{t('solutionHeader')}</span>
          </div>
        )
      }
      {errorBanners}
      {
        flagPost.showSuccess && (
          <KatAlert
            data-testid="flag-post-success"
            onDismiss={() => setFlagPost({ ...flagPost, showSuccess: false })}
            className="my-5"
            variant="success"
            header={t('request_flagPostSuccess')}
          />
        )
      }
      {
        pinThread.showSuccess && (
          <KatAlert
            data-testid="pin-thread-success"
            onDismiss={() => setPinThread({ ...pinThread, showSuccess: false })}
            className="my-5"
            header={t('pinThread_success_header')}
            variant="success"
          />
        )
      }
      <DiscussionCard
        className={isSolution ? 'bg-kat-lime' : ''}
        cardHighlight={getCardHighlight()}
        showReplies={isShowRepliesVisible ? {
          replyCount,
          expanded: !!showReplies,
          onClick: () => {
            clickMetric({
              metricValue: postId,
              metricName: ButtonTypes.displayRepliesToggle,
            });
            return dispatch(threadDetailActions.setShowReplies({
              postId,
              showReplies: !showReplies,
            }));
          },
        } : undefined}
        cardType={cardType}
        threadMetrics={<ThreadMetrics viewCount={viewCount} replyCount={replyCount} />}
        vote={(
          <Vote
            isDownVoteSelected={voteDecision === VoteOptions.downVote}
            isUpVoteSelected={voteDecision === VoteOptions.upVote}
            upVoteCount={upVoteCount}
            downVoteCount={downVoteCount}
            onUpVote={() => {
              voteSubmissionHandler(voteDecision === VoteOptions.upVote
                ? VoteOptions.unVote
                : VoteOptions.upVote);
            }}
            onDownVote={() => {
              voteSubmissionHandler(voteDecision === VoteOptions.downVote
                ? VoteOptions.unVote
                : VoteOptions.downVote);
            }}
          />
        )}
        header={(
          <DiscussionCardHeader
            isFlagged={isFlagged}
            badges={(
              <div className="flex flex-row space-x-1">
                {postType === PostType.op
                  && opPost.postState === PostState.closed
                  && (<KatIcon data-testid="badge-post-locked" name="lock_outline" size="small" className="text-kat-nordic ltr:mr-[3px] rtl:ml-[3px]" />)}
                {postType === PostType.op
                  && isPinned(opPost) && (
                    <div>
                      <KatPopover
                        kat-aria-behavior="tooltip"
                        position="top"
                        trigger-type="hover"
                        variant="tooltip"
                      >
                        {t('pinThread_pinnedByModerator', { postCount: 1 })}
                        <img src={PinIcon.src} slot="trigger" alt="pin" data-testid="badge-post-pinned" />
                      </KatPopover>
                    </div>
                )}
              </div>
            )}
            replyingToAuthor={replyToUsername}
            replyingToAuthorHref={`${basePath}${getThreadDetail(threadId)}?postId=${replyToId}`}
            title={title}
            authoredByHref={linkToProfile}
            authoredByOnClick={postAuthorClickMetric}
            group={groupId && t(groups[groupId]?.nameStringId)}
            groupHref={groupId && `${basePath}${getGroupListing(groupId)}`}
            authoredBy={postUserName}
            datePosted={(
              <PostDate
                createdAt={createdAt}
                updatedAt={updatedAt}
                updatedByModerator={!!updatedByModerator}
              />
            )}
            menuOptions={getMenuOptions()}
            avatar={(
              <Avatar
                href={linkToProfile}
                src={avatarSrc}
                onClick={postAuthorClickMetric}
              />
            )}
          />
        )}
        content={getContent()}
        category={category}
        tags={tags}
        actions={getCardAction()}
      />
      {
        flagPost.isVisible && (
          <FlagPostModal
            onDismiss={() => setFlagPost({ ...flagPost, isVisible: false })}
            postId={id}
            threadId={threadId}
            onSuccess={() => {
              dispatch(threadDetailActions.setFlagPost({ postId, isFlagged: true }));
              setFlagPost({ showSuccess: true, isVisible: false });
            }}
          />
        )
      }
      {
        pinThread.isVisible && (
          <PinThreadModal
            onDismiss={() => setPinThread({ ...pinThread, isVisible: false })}
            threadId={threadId}
            onSuccess={
              () => setPinThread({ showSuccess: true, isVisible: false })
            }
          />
        )
      }
      {
        confirmationType
        && (
          <ConfirmationModal
            postId={postId}
            threadId={threadId}
            type={confirmationType}
            onClose={() => setConfirmationType(undefined)}
          />
        )
      }
      {
        imageCarousel.visible && (
          <ModalImageCarousel
            startIndex={imageCarousel.index}
            images={imageList.map((image) => image.src)}
            onClose={() => setImageCarousel({ index: 0, visible: false })}
          />
        )
      }
    </>
  );
};

export default React.memo(DiscussionPost);
